import { usePersistentStore } from '~/stores/persistent'
import { useAgentStore } from '~/stores/agent'
import { checkRouteName } from '~/utils/routes'

/**
 * Get Enum key from value (reverse lookup)
 *
 * @param myEnum - Enum
 * @param enumValue - Value to look for
 */
export function getEnumKeyByEnumValue<T, P extends Record<string, T>> (myEnum: P, enumValue: T): keyof P | null {
  return Object.keys(myEnum).find(x => myEnum[x] === enumValue) || null
}

/**
 * Get Enum from value (reverse lookup)
 *
 * @param enumObj - Enum
 * @param str - Value to look for
 */
export function stringToEnumValue <E extends Record<string | number, unknown>>(enumObj: E, str: string, sure: true): E[keyof E]

export function stringToEnumValue <E extends Record<string | number, unknown>>(enumObj: E, str?: string): E[keyof E] | undefined

export function stringToEnumValue (enumObj: Record<string | number, unknown>, str?: string, sure?: boolean): unknown {
  if (sure) {
    return str
  }
  return Object.values(enumObj).includes(str) ? str : undefined
}

/**
 * Get current position promisified for use in async/await functions
 * ignoring coverage because it's a simple promise wrapper over browser native functionality
 */
/* istanbul ignore next */
export const getCurrentGeoPosition = (): Promise<GeolocationPosition> => new Promise((resolve, reject) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(resolve, reject)
  } else {
    throw new Error('Not supported')
  }
})

export const addCommonQueryParam = () => {
  // The router's current route, reached through tryUseNuxtApp: this runs inside
  // store and service calls, where useRoute() can report a stale route
  // (NUXT_E2005) and useRouter() throws on the server once an await has dropped
  // the Nuxt context -- as in init-auth.server.ts's ?demoid= / ?items= handling
  // and Product of the Month's second fetch. With no app there is no proxy
  // checkout to key on (it is client-only), so the query simply goes without it.
  const route = tryUseNuxtApp()?.$router.currentRoute.value
  const persistentStore = usePersistentStore()
  const agentStore = useAgentStore()
  const query: {[key: string]: string} = {}
  const sharedCartId = persistentStore.getPersistentValue('sharedCartId')
  if (sharedCartId) {
    query.sharedCartId = sharedCartId
  }
  if (agentStore.viewAsDate) {
    query.viewAsDate = agentStore.viewAsDate
  }
  // On the proxy-order checkout every cart and checkout call acts on the
  // customer's order, not the demonstrator's own cart (AB#63269).
  if (route && checkRouteName(route.name, 'customer-ordering-checkout-id')) {
    query.proxyOrderCartId = String(route.params.id)
  }
  return query
}
